import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Box, Chip, CircularProgress, Button, Tooltip,
  Select, MenuItem, // ⬅️ added
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, PictureAsPdf, Delete, Restore } from "@mui/icons-material";
import { useState } from "react";
import { dateTimeReadableSimple } from "../../utils";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

const YearRow = ({
  year,
  receipts = [],
  onView,
  onDelete,
  onRestore,
  onToggleValid,
  deleting = false,
  restoring = false,
  toggling = false,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useCustomTranslation("Donor Profile");

  return (
    <>
      <TableRow hover>
        <TableCell width={56}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{year}</TableCell>
        <TableCell align="right">{receipts.length}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("receipts_created_at")}</TableCell>
                    <TableCell>{t("receipts_by")}</TableCell>
                    <TableCell>{t("receipts_status")}</TableCell>
                    <TableCell>{t("receipts_valid")}</TableCell>
                    <TableCell align="right">{t("receipts_actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((r) => (
                    <TableRow key={r._id} hover>
                      <TableCell>{dateTimeReadableSimple(new Date(r.created_at))}</TableCell>
                      <TableCell>
                        {r.created_by?.first_name || r.created_by?.last_name
                          ? `${r.created_by?.first_name || ""} ${r.created_by?.last_name || ""}`.trim()
                          : t("receipts_user_unknown")}
                      </TableCell>
                      <TableCell>
                        {r.is_deleted ? (
                          <Chip size="small" color="warning" label={t("receipts_deleted")} />
                        ) : (
                          <Chip
                            size="small"
                            color={r.success ? "success" : "error"}
                            label={r.success ? t("receipts_sent") : t("receipts_failed")}
                          />
                        )}
                      </TableCell>

                      {/* ⬇️ Switch -> Select (with i18n labels) */}
                      <TableCell>
                        <Select
                          size="small"
                          value={r.is_valid ? "valid" : "not_valid"}
                          onChange={(e) => onToggleValid(r, e.target.value === "valid")}
                          disabled={toggling}
                        >
                          <MenuItem value="valid">{t("receipts_valid")}</MenuItem>
                          <MenuItem value="not_valid">{t("receipts_not_valid")}</MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell align="right">
                        {!r.is_deleted && (
                          <Tooltip title={t("receipts_view")}>
                            <span>
                              <Button size="small" startIcon={<PictureAsPdf />} onClick={() => onView(r)}>
                                {t("receipts_view")}
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        {!r.is_deleted ? (
                          <Tooltip title={t("receipts_delete")}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDelete(r)}
                                disabled={deleting}
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("receipts_restore")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onRestore(r)}
                                disabled={restoring}
                              >
                                <Restore />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ReceiptsByYearTable = ({
  data,
  loading,
  onView,
  onDelete,
  onRestore,
  onToggleValid,
  deleting,
  restoring,
  toggling,
}) => {
  const { t } = useCustomTranslation("Donor Profile");
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>{t("receipts_year")}</TableCell>
            <TableCell align="right">{t("receipts_count")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          ) : (
            (data || []).map((group) => (
              <YearRow
                key={group.year}
                year={group.year}
                receipts={group.receipts}
                onView={onView}
                onDelete={onDelete}
                onRestore={onRestore}
                onToggleValid={onToggleValid}
                deleting={deleting}
                restoring={restoring}
                toggling={toggling}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReceiptsByYearTable;
